import { useQueryClient } from "@tanstack/react-query";
import { format, isValid, parse } from "date-fns";
import { parsePhoneNumber } from "libphonenumber-js";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";

import {
  getGetActiveParticipantAccountPaymentAccountGetQueryKey,
  getListCardsPaymentAccountCardAllGetQueryKey,
  type PaymentAccountCreate,
  type DefaultableCardType,
  type PaymentProvider,
  useCreatePaymentAccountPaymentAccountPost,
  useSetDefaultCardPaymentAccountPaymentMethodTypeProviderDefaultPost,
} from "@/api-v3";
import { useErrorHandler } from "@/hooks/use-error-handler";
import { useToast } from "@/hooks/use-toast";
import {
  getApiValidationErrors,
  isApiValidationError,
} from "@/lib/api-validation-errors";
import { removeSpacesAndHyphens, stripTextAfterHyphen } from "@/lib/utils";
import { useAppDispatch, useAppSelector } from "@/store";

import {
  findFirstFormWithErrors,
  resolveServerFieldErrors,
} from "../helpers/server-field-errors";
import { useAvailablePaymentMethods } from "../hooks/use-available-payment-methods";
import { useCompletePaymentMethodChange } from "../hooks/use-complete-payment-method-change";
import {
  updateFormData,
  resetForm,
  selectPaymentMethodFormData,
  clearServerFieldErrors,
  setServerFieldErrors,
} from "../store/payment-method-form-slice";
import {
  getCardFormFactor,
  getCardTypeForPaymentType,
  type NewCardSuccessState,
} from "../types/available-payment-methods";

import { PaymentMethodFormsContext } from "./use-payment-method-forms";

const MASSPAY_STATIC_DATE_FORMAT = "yyyy-MM-dd";

interface PaymentMethodFormsProviderProps {
  children: ReactNode;
  onSubmit: (values: Record<string, string>) => void;
}

export const PaymentMethodFormsProvider = (
  props: PaymentMethodFormsProviderProps
) => {
  const { children, onSubmit } = props;
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { handleMutationError } = useErrorHandler();
  const { paymentMethodName } = useParams<{ paymentMethodName: string }>();
  const queryClient = useQueryClient();
  const [acknowledgedLegalDocumentIds, setAcknowledgedLegalDocumentIds] =
    useState<string[]>([]);
  const { availablePaymentMethods } = useAvailablePaymentMethods();
  const formData = useAppSelector(selectPaymentMethodFormData);
  const { completePaymentMethodChange } = useCompletePaymentMethodChange();

  const paymentMethod = availablePaymentMethods.find(
    (method) => method.method_provider_name === paymentMethodName
  );

  // Acknowledgements belong to one method; switching methods starts over.
  useEffect(() => {
    setAcknowledgedLegalDocumentIds([]);
  }, [paymentMethodName]);

  const toggleLegalDocumentAcknowledged = (
    documentId: string,
    acknowledged: boolean
  ) => {
    setAcknowledgedLegalDocumentIds((current) =>
      acknowledged
        ? Array.from(new Set([...current, documentId]))
        : current.filter((id) => id !== documentId)
    );
  };

  const allLegalDocumentsAcknowledged = (
    paymentMethod?.legal_documents ?? []
  ).every((document) => acknowledgedLegalDocumentIds.includes(document.id));

  const updateFormDataHandler = (
    formName: string,
    newData: Record<string, string>
  ) => {
    dispatch(updateFormData({ formName, data: newData }));
  };

  const getAllFormData = () => {
    return Object.values(formData).reduce(
      (acc, formValues) => ({
        ...acc,
        ...formValues,
      }),
      {}
    );
  };

  const formatFormData = (formData: Record<string, Record<string, string>>) => {
    if (!paymentMethod?.form) {
      return formData;
    }

    const firstFormName = paymentMethod.form.name;
    const result: Record<string, string | Record<string, string>> = {};

    // Process all form data
    Object.entries(formData).forEach(([formName, formValues]) => {
      // First trim all string values
      const trimmedValues = Object.fromEntries(
        Object.entries(formValues).map(([key, value]) => [
          key,
          typeof value === "string" ? value.trim() : value,
        ])
      );

      if (formName === firstFormName) {
        // Flatten first form data
        Object.assign(result, trimmedValues);
      } else {
        // Keep other forms nested under their names
        result[formName] = trimmedValues;
      }
    });

    // TOOD: ask BE team to handle this
    // Process zip code if it exists
    if (result.zip) {
      result.zip = removeSpacesAndHyphens(
        stripTextAfterHyphen(result.zip as string)
      );
    }

    // Format date to the static format
    if (result.date_of_birth) {
      result.date_of_birth = format(
        new Date(result.date_of_birth as string),
        MASSPAY_STATIC_DATE_FORMAT
      );
    }

    // Transform expiration_date from MM/YY (Evervault) to YYYY-MM (API)
    if (result.expiration_date && typeof result.expiration_date === "string") {
      const parsed = parse(result.expiration_date, "MM/yy", new Date());
      if (isValid(parsed)) {
        result.expiration_date = format(parsed, "yyyy-MM");
      }
    }

    // TOOD: ask BE team to handle this
    // Process phone number if it exists
    if (result.phone) {
      const cleanedPhone = (result.phone as string).replace(/\s+/g, "");
      try {
        const phoneObject = parsePhoneNumber(cleanedPhone);
        result.phone = phoneObject.nationalNumber;
        result.phoneCountryCode = `+${phoneObject.countryCallingCode}`;
      } catch (error) {
        console.error("Error processing phone number:", error);
      }
    }

    return result;
  };

  const { mutate: setDefaultCard, isPending: isSettingDefaultPaymentMethod } =
    useSetDefaultCardPaymentAccountPaymentMethodTypeProviderDefaultPost({
      mutation: {
        onError: (error) => {
          handleMutationError(error);
        },
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: getGetActiveParticipantAccountPaymentAccountGetQueryKey(),
          });
        },
      },
    });

  const setDefaultPaymentMethod = (
    paymentMethodType: DefaultableCardType,
    provider: PaymentProvider,
    onSuccess?: () => void
  ) => {
    setDefaultCard(
      { paymentMethodType, provider },
      {
        onSuccess: () => {
          onSuccess?.();
        },
      }
    );
  };

  const getFormPath = (formName: string) => {
    const base = `/payment-methods/${paymentMethod?.method_provider_name}/create`;
    return formName === paymentMethod?.form.name ? base : `${base}/${formName}`;
  };

  const handleCreationError = (error: unknown) => {
    if (!isApiValidationError(error) || !paymentMethod) {
      handleMutationError(error);
      return;
    }

    const { fieldErrors, formErrors } = getApiValidationErrors(error);
    const { byForm, unresolved } = resolveServerFieldErrors(
      paymentMethod.form,
      fieldErrors
    );
    const targetForm = findFirstFormWithErrors(paymentMethod.form, byForm);

    const toastMessages = [...unresolved, ...formErrors]
      .map(({ message }) => message.trim())
      .filter(Boolean);

    if (targetForm) {
      dispatch(setServerFieldErrors(byForm));
    }

    toast({
      description:
        toastMessages.length > 0
          ? toastMessages.join("\n")
          : t("please_check_your_details_above"),
      variant: "destructive",
    });

    if (targetForm) {
      navigate(getFormPath(targetForm));
    }
  };

  const {
    mutateAsync: createPaymentAccountAsync,
    isPending: isCreatingPaymentAccount,
  } = useCreatePaymentAccountPaymentAccountPost({
    request: { skipGlobalErrorToast: true },
    mutation: {
      onError: (error) => {
        handleCreationError(error);
      },
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getGetActiveParticipantAccountPaymentAccountGetQueryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: getListCardsPaymentAccountCardAllGetQueryKey(),
          }),
        ]);
        handleCreationSuccess();
      },
    },
  });

  const handlePayment = async (
    formattedFormData: Record<string, Record<string, string>>
  ) => {
    const formattedValues = formatFormData(formattedFormData);

    const body: PaymentAccountCreate = {
      ...formattedValues,
      payment_type: paymentMethod!.payment_type.toLowerCase(),
      provider: paymentMethod!.provider.toLowerCase(),
      accepted_tc: true,
    };

    await createPaymentAccountAsync({ data: body });
  };

  // Where a creation lands depends only on the form factor of the Card Type
  // just created, never on which cards the participant already holds: every
  // virtual type shows the new-card success page, told which Card Type was
  // created so it shows that card, every physical type the shipping steps.
  // Anything that is not a card completes the change.
  const handleCreationSuccess = useCallback(() => {
    dispatch(resetForm());
    const cardType = paymentMethod
      ? getCardTypeForPaymentType(paymentMethod.payment_type)
      : null;
    const formFactor = paymentMethod
      ? getCardFormFactor(paymentMethod.payment_type)
      : null;

    if (cardType && formFactor === "virtual") {
      const state: NewCardSuccessState = { cardType };
      navigate("/payment-methods/new-card-success", { replace: true, state });
    } else if (formFactor === "physical") {
      navigate("/card/physical/success", { replace: true });
    } else {
      completePaymentMethodChange();
    }
  }, [paymentMethod, navigate, dispatch, completePaymentMethodChange]);

  const submitFormData = async () => {
    if (!paymentMethod) {
      throw new Error("Payment method not set");
    }

    dispatch(clearServerFieldErrors());
    await handlePayment(formData);
    onSubmit(getAllFormData());
  };

  return (
    <PaymentMethodFormsContext.Provider
      value={{
        formData,
        paymentMethod,
        updateFormData: updateFormDataHandler,
        submitFormData,
        isSubmitting: isCreatingPaymentAccount,
        getAllFormData,
        setDefaultPaymentMethod,
        isSettingDefaultPaymentMethod,
        acknowledgedLegalDocumentIds,
        toggleLegalDocumentAcknowledged,
        allLegalDocumentsAcknowledged,
        completePaymentMethodChange,
      }}
    >
      {children}
    </PaymentMethodFormsContext.Provider>
  );
};
